import { describeError } from '../errorMessage';
import {
    createBeteiligter,
    ensureAsplan,
    fetchAsplan,
    fetchAsplanBundle,
    updateAsplan,
    type SchreibKontext,
} from '../asplanService';
import { baueSteckbrief, OHNE_LESEFEHLER, type ProjektSteckbrief } from '../projektSteckbrief/steckbrief';
import { ergaenzeHistorie } from '../../components/asplan/asplanLogic';
import { planeUebernahme, type BeteiligtenVorlage } from '../../components/asplan/bestandUebernahme';
import { objektBeschreibung } from '../../components/asplan/ki/historischeErkundung';
import { baubeschreibungHinweis, bedarfsanzeige } from '../../components/asplan/planungBedarf';
import type { Asplan, Beteiligter } from '../../components/asplan/types';
import { ASPLAN_VERDACHT_WEGE, pflichtenStand, type AsplanWeg } from '../../components/planungsphase/planungLogic';
import type { ProjektMerkmale } from '../../components/planungsphase/types';
import type { ReportMetadata } from '../../types';
import type { BestandStand, ErzeugungsErgebnis, FolgeErzeuger, PlanungsQuelle } from './typen';

/**
 * Folgeunterlage „A+S-Plan" — der Entwurf aus der Planungsphase.
 *
 * ═══ WAS HIER GESCHIEHT ═══════════════════════════════════════════════════
 * Ist die Planungsphase erfuellt, legt die App den A+S-Plan als ENTWURF an
 * (`ensureAsplan` — Katalogfassung, Regelwerk und die Schutzklassen-Vorlagen
 * wie beim Knopf im Modul) und fuellt ihn mit dem, was die Vorschlaege der
 * Stufe 3 und die Projektdaten hergeben:
 *
 *   objekt.beschreibung ← Baubeschreibung der Planungsphase
 *                         (`baubeschreibungHinweis`, nur `belegt`)
 *   asplan_beteiligte   ← Vertreterliste, Bauherr, Auftraggeber
 *                         (`planeUebernahme` — dieselbe Ableitung wie der
 *                         Knopf „Aus dem Projekt uebernehmen", `quelle =
 *                         'projekt'`, also spaeter nachziehbar)
 *   historie            ← ein Eintrag „Entwurf aus der Planungsphase angelegt"
 *
 * ═══ BEDARF UND VERDACHTSWEGE WERDEN NICHT GESPEICHERT ═══════════════════
 * Der A+S-Bedarf ist eine LIVE-Anzeige im Modulkopf (`bedarfsanzeige` aus
 * dem Steckbrief). Ein Schnappschuss am Plan waere eine zweite Wahrheit, die
 * beim naechsten Merkmalswechsel veraltet. Der Erzeuger nennt den Anlass
 * deshalb nur in der Ergebnisliste — bei `pflicht` mit den Wegen, die ihn
 * ausloesen (Gebaeudeschadstoffe, Boden/Altlasten; Entscheid E4).
 *
 * ═══ WAS HIER NICHT GESCHIEHT ═════════════════════════════════════════════
 *   - keine KI (die Historische Erkundung laeuft nur auf Klick im Modul);
 *   - Kapitel 9 nicht aus dem Unterlagenstatus: ob ein Beleg in der Ablage
 *     einsehbar ist, entscheidet `steckbriefAnlagen` gegen die Projektablage
 *     — die liest dieser Erzeuger nicht; er nennt die Zahl als offenen Punkt;
 *   - keine Freigabe, keine Ablage, kein Versand, keine Buchung;
 *   - NIE ueberschreiben: gibt es schon einen Plan, bleibt er (`vorhanden`).
 *
 * Saetze und Patch entstehen pur (`baueAsplanEntwurf`); die Supabase-Aufrufe
 * laufen ausschliesslich ueber `asplanService`.
 */

const ART = 'asplan' as const;

// ═══════════════════════════════════════════════════════════════════════════
// PUR — Steckbrief, Bestandstext, Verdachtswege, Entwurf
// ═══════════════════════════════════════════════════════════════════════════

/** Der Steckbrief aus den gespeicherten Staenden, die der Aufrufer mitgibt. */
export const steckbriefAusQuelle = (q: PlanungsQuelle): ProjektSteckbrief =>
    baueSteckbrief({
        metadata: q.projektDaten,
        phase: q.phase,
        fragen: q.fragen,
        // Die Mitgeltenden des SiGe-Plans entscheiden nur, ob eine Unterlage
        // als „erforderlich" gilt — hier wird nur der Stand `liegt_vor` gezaehlt.
        sigeSchritte: [],
        lesefehler: { ...OHNE_LESEFEHLER },
    }, q.heuteIso);

/** TT.MM.JJJJ — ISO-Tag zerlegt, Zeitstempel in Ortszeit. */
const tagDeutsch = (wert: string | null | undefined): string => {
    const s = String(wert || '').trim();
    if (!s) return '';
    const tag = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (tag) return `${tag[3]}.${tag[2]}.${tag[1]}`;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
};

/** Kurzbeschreibung des Bestands — „Entwurf vom 12.10.2026", „Freigegeben am 14.10.2026 (Rev. 1)". */
export const asplanBestandText = (
    p: Pick<Asplan, 'status' | 'stand' | 'created_at' | 'freigegeben_am' | 'rev_label'>,
): string => {
    if (p.status === 'freigegeben') {
        const am = tagDeutsch(p.freigegeben_am);
        const rev = String(p.rev_label || '').trim();
        return `Freigegeben${am ? ` am ${am}` : ''}${rev ? ` (${rev})` : ''}`;
    }
    const vom = tagDeutsch(p.stand) || tagDeutsch(p.created_at);
    return vom ? `Entwurf vom ${vom}` : 'Entwurf ohne Datum';
};

/**
 * Traegt der Plan nur, was `ensureAsplan` beim Anlegen schreibt? Gibt
 * `ensureAsplan` einen Plan zurueck, den eine zweite Sitzung zwischen
 * Pruefung und Anlegen schon bearbeitet hat, bleibt er unberuehrt.
 */
export const asplanIstUnberuehrt = (p: Asplan): boolean =>
    p.status === 'entwurf'
    && (p.historie || []).length === 0
    && !objektBeschreibung(p.objekt);

const WEG_LABEL: Record<AsplanWeg, string> = {
    gebaeudeschadstoffe: 'Gebäudeschadstoffe',
    altlasten: 'Boden/Altlasten',
};

/**
 * Die Verdachtswege, die fuer sich allein `pflicht` ergeben.
 *
 * Keine zweite Regel: je Weg werden nur SEINE Merkmale an `pflichtenStand`
 * gegeben. Der andere Weg ist dann `ungeklaert` und kann den Schluss nicht
 * auf `pflicht` heben — `pflicht` kommt also genau aus diesem Weg.
 */
export const verdachtsWege = (merkmale: ProjektMerkmale, heuteIso: string): string[] =>
    ASPLAN_VERDACHT_WEGE
        .filter(w => {
            const teil = Object.fromEntries(
                w.merkmale.filter(k => merkmale[k] !== undefined).map(k => [k, merkmale[k]]),
            ) as ProjektMerkmale;
            return pflichtenStand({ merkmale: teil }, heuteIso).find(p => p.art === 'asplan')?.stand === 'pflicht';
        })
        .map(w => WEG_LABEL[w.weg]);

export interface AsplanEntwurf {
    /** Aenderung am Kopfsatz — immer mit Historieneintrag. */
    patch: Partial<Asplan>;
    /** Saetze, die unabhaengig vom Patch gelten. */
    uebernommen: string[];
    /** Saetze, die nur gelten, wenn der Patch geschrieben wurde. */
    ausPatch: string[];
    offen: string[];
    /** Neu anzulegende Beteiligte (`quelle = 'projekt'`). */
    beteiligte: BeteiligtenVorlage[];
}

/**
 * Der Entwurf zu einem frisch angelegten Plan — pur.
 *
 * `opt.vorhandeneBeteiligte` sind die Beteiligten, die der Plan unmittelbar
 * vor dem Schreiben TATSAECHLICH traegt. Ein eben angelegter Plan hat keine;
 * hat aber ein zweites Fenster ihn zeitgleich angelegt und schon Beteiligte
 * uebernommen, sieht der Kopfsatz trotzdem unberuehrt aus — dann wird nur
 * angelegt, was noch fehlt (`planeUebernahme` wie der Knopf im Modul).
 */
export const baueAsplanEntwurf = (
    sb: ProjektSteckbrief,
    plan: Asplan,
    metadata: Partial<ReportMetadata> | null,
    opt: { heuteIso: string; bearbeiter: string; vorhandeneBeteiligte?: readonly Beteiligter[] },
): AsplanEntwurf => {
    const patch: Partial<Asplan> = {};
    const uebernommen: string[] = [];
    const ausPatch: string[] = [];
    const offen: string[] = [];

    // ─── Anlass aus der Planungsphase (live, nicht gespeichert) ──────────
    const bedarf = bedarfsanzeige(sb);
    if (bedarf?.stand === 'pflicht') {
        const wege = verdachtsWege(sb.merkmale, opt.heuteIso);
        uebernommen.push(wege.length
            ? `Anlass laut Planungsphase: Verdacht über ${wege.join(' und ')} — A+S-Plan erforderlich.`
            : 'Anlass laut Planungsphase: A+S-Plan erforderlich.');
    } else if (bedarf?.stand === 'keine') {
        offen.push('Laut Planungsphase ist kein A+S-Plan erforderlich — den Entwurf nur weiterführen, '
            + 'wenn Erkundung oder Befunde einen Verdacht ergeben.');
    } else if (bedarf?.stand === 'lesefehler') {
        offen.push(`${bedarf.titel} — den Bedarf im Modulkopf prüfen.`);
    } else {
        offen.push('A+S-Bedarf in der Planungsphase noch offen — die fehlenden Angaben nennt der Modulkopf.');
    }

    // ─── Beschreibung des Bauvorhabens ───────────────────────────────────
    const hinweis = baubeschreibungHinweis(sb, objektBeschreibung(plan.objekt));
    if (hinweis?.art === 'vorschlag' && !hinweis.vorbehalt) {
        patch.objekt = { ...(plan.objekt || {}), beschreibung: hinweis.text };
        ausPatch.push('Beschreibung des Bauvorhabens aus der Planungsphase übernommen.');
    } else if (hinweis?.art === 'vorschlag') {
        offen.push(`Beschreibung des Bauvorhabens prüfen: ${hinweis.vorbehalt} Der Vorschlag steht im Reiter „Grundlagen“.`);
    } else if (hinweis?.art === 'fehlt') {
        offen.push(`Beschreibung des Bauvorhabens eintragen: ${hinweis.grund}`);
    }

    // Herkunft festhalten — auch ohne Beschreibung: der Plan ist nicht von
    // Hand angelegt worden, und das soll man ihm ansehen.
    patch.historie = ergaenzeHistorie(plan.historie || [], {
        datum: opt.heuteIso,
        autor: opt.bearbeiter,
        aenderungen: 'Entwurf aus der Planungsphase angelegt',
    });

    // ─── Beteiligte aus den Projektdaten ─────────────────────────────────
    // Gegen den TATSAECHLICHEN Bestand gerechnet, nicht gegen „eben angelegt,
    // also leer" — siehe Kommentar oben.
    const vorhandene = [...(opt.vorhandeneBeteiligte || [])];
    const uebernahme = planeUebernahme(metadata, vorhandene);
    const beteiligte = uebernahme.neu;
    const ausProjekt = beteiligte.length + uebernahme.aktualisieren.length
        + uebernahme.unveraendert.length + uebernahme.bereitsManuell.length;
    if (!ausProjekt) {
        offen.push('Beteiligte eintragen: Vertreterliste und Bauherr sind in den Projektdaten nicht gepflegt.');
    }
    if (vorhandene.length) {
        offen.push(`Im Plan ${vorhandene.length === 1 ? 'stand schon ein Beteiligter' : `standen schon ${vorhandene.length} Beteiligte`} `
            + '— vermutlich zeitgleich in einem zweiten Fenster eingetragen; nur Fehlende aus den Projektdaten ergänzt. '
            + 'Im Reiter „Beteiligte“ prüfen.');
    }
    offen.push('Koordinator nach DGUV Regel 101-004 im Reiter „Beteiligte“ eintragen — die Vertreterliste nennt nur den SiGeKo.');

    // ─── Kapitel 9 ───────────────────────────────────────────────────────
    const belegt = sb.unterlagen.filter(u => u.asplan && u.status === 'liegt_vor' && u.dokument_id).length;
    offen.push(belegt
        ? `${belegt === 1 ? 'Eine Unterlage' : `${belegt} Unterlagen`} der Planungsphase ${belegt === 1 ? 'liegt' : 'liegen'} `
            + 'mit Beleg vor — im Reiter „Beteiligte“ unter „Unterlagen und Anlagen“ in Kapitel 9 übernehmen.'
        : 'Unterlagen für Kapitel 9 benennen (Gutachten, Untersuchungsberichte).');

    // ─── Was nur ein Mensch erledigt ─────────────────────────────────────
    offen.push('Historische Erkundung erarbeiten: Nutzungsgeschichte, Gebäudeschadstoffe, nicht ermittelbare Angaben.');
    offen.push('Befunde, Arbeitsbereiche und Tätigkeiten erfassen und den Schutzklassen zuordnen.');

    return { patch, uebernommen, ausPatch, offen, beteiligte };
};

const beteiligteSatz = (n: number): string =>
    n === 1
        ? 'Ein Beteiligter aus Vertreterliste und Projektdaten übernommen.'
        : `${n} Beteiligte aus Vertreterliste und Projektdaten übernommen.`;

// ═══════════════════════════════════════════════════════════════════════════
// DUENN — Lesen und Schreiben ueber asplanService
// ═══════════════════════════════════════════════════════════════════════════

const OHNE_PROJEKT = 'Ohne gespeichertes Projekt kann kein A+S-Plan angelegt werden.';

type Lage =
    | { status: 'keiner' }
    | { status: 'vorhanden'; plan: Asplan }
    | { status: 'fehler'; fehler: string };

/** Liest den Kopfsatz — NUR lesen (`fetchAsplan` legt nichts an). */
const leseLage = async (projectId: string): Promise<Lage> => {
    try {
        const plan = await fetchAsplan(projectId);
        return plan ? { status: 'vorhanden', plan } : { status: 'keiner' };
    } catch (e) {
        return { status: 'fehler', fehler: `Der A+S-Plan konnte nicht gelesen werden: ${describeError(e)}` };
    }
};

const bestand = async (projectId: string): Promise<BestandStand> => {
    if (!projectId || projectId === 'temp-id') return { status: 'keiner' };
    const lage = await leseLage(projectId);
    if (lage.status === 'vorhanden') return { status: 'vorhanden', beschreibung: asplanBestandText(lage.plan) };
    return lage;
};

const legeAn = async (q: PlanungsQuelle): Promise<ErzeugungsErgebnis> => {
    if (!q.projectId || q.projectId === 'temp-id') return { status: 'fehler', art: ART, fehler: OHNE_PROJEKT };

    // Unmittelbar vor dem Schreiben noch einmal: gibt es ihn, bleibt er.
    const lage = await leseLage(q.projectId);
    if (lage.status === 'fehler') {
        return { status: 'fehler', art: ART, fehler: `Kein A+S-Plan angelegt. ${lage.fehler}` };
    }
    if (lage.status === 'vorhanden') {
        return { status: 'vorhanden', art: ART, beschreibung: asplanBestandText(lage.plan) };
    }

    const sb = steckbriefAusQuelle(q);
    // Den Nur-Ansicht-Schutz traegt der Aufrufer; der Vertrag reicht ihn
    // nicht mit. Die RLS der Tabellen bleibt davon unberuehrt.
    const ctx: SchreibKontext = { nurAnsicht: false };
    const offen: string[] = [];

    let plan: Asplan;
    let schutzklassen = true;
    try {
        plan = await ensureAsplan(ctx, q.projectId, `A+S-Plan ${q.projektDaten?.projectName ?? ''}`.trim());
    } catch (e) {
        // `ensureAsplan` schreibt erst den Kopfsatz, dann die Schutzklassen.
        // Scheitert der zweite Schritt, steht der Plan schon — das ist eine
        // Teilanlage, kein Totalausfall.
        const nachher = await fetchAsplan(q.projectId).catch(() => null);
        if (!nachher) {
            return { status: 'fehler', art: ART, fehler: `Der A+S-Plan konnte nicht angelegt werden: ${describeError(e)}` };
        }
        plan = nachher;
        schutzklassen = false;
        offen.push(`Der Plan ist angelegt, aber nicht vollständig: ${describeError(e)} Bitte die Schutzklassen im Reiter „Schutzklassen“ prüfen.`);
    }
    if (!asplanIstUnberuehrt(plan)) {
        return { status: 'vorhanden', art: ART, beschreibung: asplanBestandText(plan) };
    }

    // Die Beteiligten, die der Plan JETZT traegt — vor dem Schreiben gelesen.
    // Nicht lesbar → keine Beteiligten anlegen (sonst womoeglich doppelt).
    let vorhandene: Beteiligter[] | null = null;
    // Der frisch gelesene Plan ist die Grundlage des Patches (Objekt,
    // Historie) — nicht der Rueckgabewert von `ensureAsplan`, der veraltet
    // sein kann, wenn ein zweites Fenster dazwischen geschrieben hat.
    let frisch = plan;
    try {
        const bundle = await fetchAsplanBundle(q.projectId);
        if (bundle && bundle.plan.id === plan.id) {
            vorhandene = bundle.beteiligte;
            frisch = bundle.plan;
        } else {
            offen.push('Beteiligte nicht übernommen — der Plan ließ sich nicht zurücklesen. Im Reiter „Beteiligte“ aus dem Projekt übernehmen.');
        }
    } catch (e) {
        offen.push(`Beteiligte nicht übernommen — die vorhandenen Beteiligten ließen sich nicht lesen: ${describeError(e)} `
            + 'Im Reiter „Beteiligte“ aus dem Projekt übernehmen.');
    }
    if (!asplanIstUnberuehrt(frisch)) {
        return { status: 'vorhanden', art: ART, beschreibung: asplanBestandText(frisch) };
    }

    const entwurf = baueAsplanEntwurf(sb, frisch, q.projektDaten, {
        heuteIso: q.heuteIso,
        bearbeiter: q.bearbeiter,
        vorhandeneBeteiligte: vorhandene || [],
    });

    let patchGeschrieben = true;
    try {
        await updateAsplan({ ...ctx, status: 'entwurf' }, frisch.id, entwurf.patch);
    } catch (e) {
        patchGeschrieben = false;
        offen.push(`Die Angaben aus der Planungsphase wurden nicht eingetragen: ${describeError(e)} `
            + 'Die Beschreibung des Bauvorhabens im Reiter „Grundlagen“ übernehmen.');
    }

    let angelegt = 0;
    let beteiligtenFehler = '';
    const neue = vorhandene ? entwurf.beteiligte : [];
    // Hinter die vorhandenen Zeilen sortiert, nicht dazwischen.
    const sortierAb = (vorhandene || []).reduce((max, b) => Math.max(max, (b.sortierung ?? -1) + 1), 0);
    for (const v of neue) {
        try {
            await createBeteiligter(ctx, {
                project_id: plan.project_id,
                asplan_id: plan.id,
                ...v,
                gilt_fuer: [],
                quelle: 'projekt',
                sortierung: sortierAb + angelegt,
            });
            angelegt += 1;
        } catch (e) {
            if (!beteiligtenFehler) beteiligtenFehler = describeError(e);
        }
    }
    if (beteiligtenFehler) {
        offen.push(`${neue.length - angelegt} von ${neue.length} Beteiligten nicht übernommen: `
            + `${beteiligtenFehler} Im Reiter „Beteiligte“ erneut aus dem Projekt übernehmen.`);
    }

    return {
        status: 'angelegt',
        art: ART,
        uebernommen: [
            schutzklassen
                ? 'Entwurf des A+S-Plans angelegt, Schutzklassen aus dem Katalog vorbelegt.'
                : 'Entwurf des A+S-Plans angelegt.',
            ...entwurf.uebernommen,
            ...(patchGeschrieben ? entwurf.ausPatch : []),
            ...(angelegt ? [beteiligteSatz(angelegt)] : []),
        ],
        offen: [...offen, ...entwurf.offen],
    };
};

export const asplanErzeuger: FolgeErzeuger = {
    art: ART,
    titel: 'A+S-Plan',
    bestand,
    legeAn,
};
